// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
	OnQrCodeShared,
	QrCodeLayout,
	QrCodePoster,
	QrCodeTracking,
	QrViewCloseReason,
	QrViewSource,
	ShareFileMethod,
	SlideUpQrCode,
} from '#src/app/pages/psf/promoteOrShare/shared/hooks';

import { CHANNEL_GRID_TRACKING_DATA } from './constants';

/**
 * The slide-up's views. `grid` is the production share grid; the others are the QR code experiment's
 * views (psf_promote_or_share_qr_code_experiment), rendered inside the same modal.
 */
type SlideUpStep = 'grid' | 'qr' | 'poster';

/**
 * Resets to the grid on every open. The reset runs during render, not in an effect, so an open never
 * flashes the last view and a close keeps the view through the exit animation:
 * https://react.dev/learn/you-might-not-need-an-effect
 */
function useSlideUpStep(isOpen: boolean) {
	const [step, setStep] = useState<SlideUpStep>('grid');
	const [wasOpen, setWasOpen] = useState(isOpen);
	if (wasOpen !== isOpen) {
		setWasOpen(isOpen);
		if (isOpen) setStep('grid');
	}
	return { step, setStep };
}

type QrCodeProps = Readonly<{
	qrCodeLayout: QrCodeLayout | null | undefined;
	qrCode: SlideUpQrCode | undefined;
	qrCodeTracking: QrCodeTracking | undefined;
	qrCodePoster: QrCodePoster | undefined;
	onQrCodeShared: OnQrCodeShared | undefined;
	cancelQrRedirect: (() => void) | undefined;
}>;

/**
 * What the current step renders. The tile, and so the QR view, needs the arm, the code, and the
 * tracking; Print poster, and so the poster view, also needs `variant_2` and what the poster is built
 * from.
 */
function getQrCodeViews(
	step: SlideUpStep,
	qrViewSource: QrViewSource,
	{ qrCodeLayout, qrCode, qrCodeTracking, qrCodePoster }: QrCodeProps,
) {
	if (!qrCodeLayout || !qrCode || !qrCodeTracking) {
		return { showQrTile: false, qrView: null, posterView: null };
	}
	const poster = qrCodeLayout.showPrint ? qrCodePoster : undefined;
	return {
		showQrTile: true,
		qrView: step === 'qr' ? { qrCode, showPrint: !!poster, tracking: qrCodeTracking, source: qrViewSource } : null,
		posterView: step === 'poster' && poster ? poster : null,
	};
}

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * The QR code views' state. The poster view is reachable only in `variant_2` (`showPrint`).
 *
 * Leaving the QR view records why, and `getExitReason` reads it when the view unmounts, so its close
 * event names the exit. A slide-up close leaves no reason here: the view sees `isOpen` turn false.
 *
 * A Save or Print that delivers its file redirects to the share page. Save records `redirect` first, so
 * the QR view's close event names it when the navigation unmounts the view. A later back or Print
 * poster still overwrites it, because the download redirect waits before it navigates.
 *
 * Print poster cancels that pending download redirect (`cancelQrRedirect`), so the redirect does not
 * leave the page while the poster view builds the PDF. Back keeps it: on the grid, the supporter is
 * done with the QR view, and a channel tap there supersedes the redirect, as it does after Copy link.
 * A tile tap there cancels it too, and a pending Copy link redirect, so neither leaves the reopened QR
 * view.
 */
export function useQrCodeSteps(isOpen: boolean, qrCodeProps: QrCodeProps) {
	const { step, setStep } = useSlideUpStep(isOpen);
	const [qrViewSource, setQrViewSource] = useState<QrViewSource>('tile');
	const exitReasonRef = useRef<QrViewCloseReason | null>(null);
	const qrTileRef = useRef<HTMLDivElement>(null);
	const shouldFocusQrTileRef = useRef(false);

	/**
	 * Each QR view starts with no exit reason and records how it opened, for its view event. Back from
	 * the poster view remounts the QR view without a tile tap, so its view event says `poster_back`.
	 */
	const enterQrView = useCallback(
		(source: QrViewSource) => {
			exitReasonRef.current = null;
			setQrViewSource(source);
			setStep('qr');
		},
		[setStep],
	);
	const { cancelQrRedirect } = qrCodeProps;
	const openQrView = useCallback(() => {
		qrCodeProps.qrCodeTracking?.trackTileTapped(CHANNEL_GRID_TRACKING_DATA);
		cancelQrRedirect?.();
		enterQrView('tile');
	}, [qrCodeProps.qrCodeTracking, cancelQrRedirect, enterQrView]);
	// Back from the poster view cancels a Print download's pending redirect, so the redirect does not
	// leave the QR view the supporter just went back to, with a close event that misses `redirect`.
	const showQrView = useCallback(() => {
		cancelQrRedirect?.();
		enterQrView('poster_back');
	}, [cancelQrRedirect, enterQrView]);
	const leaveQrView = useCallback(
		(nextStep: SlideUpStep, reason: QrViewCloseReason) => {
			exitReasonRef.current = reason;
			setStep(nextStep);
		},
		[setStep],
	);
	const showGrid = useCallback(() => {
		shouldFocusQrTileRef.current = true;
		leaveQrView('grid', 'back');
	}, [leaveQrView]);
	const openPosterView = useCallback(() => {
		cancelQrRedirect?.();
		leaveQrView('poster', 'poster');
	}, [cancelQrRedirect, leaveQrView]);

	// The back button unmounts with the QR view, so focus returns to the tile.
	useEffect(() => {
		if (step !== 'grid' || !shouldFocusQrTileRef.current) return;
		shouldFocusQrTileRef.current = false;
		qrTileRef.current?.querySelector('button')?.focus();
	}, [step]);
	const getExitReason = useCallback(() => exitReasonRef.current, []);

	const { onQrCodeShared } = qrCodeProps;
	const onQrCodeSaved = useCallback(
		(method: ShareFileMethod) => {
			exitReasonRef.current = 'redirect';
			onQrCodeShared?.('qr_code', method);
		},
		[onQrCodeShared],
	);
	const onPosterShared = useCallback(
		(method: ShareFileMethod) => onQrCodeShared?.('qr_poster', method),
		[onQrCodeShared],
	);

	return {
		...getQrCodeViews(step, qrViewSource, qrCodeProps),
		qrTileRef,
		openQrView,
		showGrid,
		openPosterView,
		showQrView,
		getExitReason,
		onQrCodeSaved,
		onPosterShared,
	};
}
